import type { AppDispatch } from "_shared/_stores/store-types";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { getNetworkConnectionStatusByUserId } from "users/networking/_stores/network-connections-redux-slice";
import { userNetworkConnectionsApi } from "users/networking/connections/_api/user-network-connection-api";

/** The most ids that `POST users/network-connections/by-user-ids` accepts in one request. */
export const NETWORK_CONNECTION_STATUS_BATCH_SIZE = 100;

const pendingUserIds = new Set<string>();
const requestedUserIds = new Set<string>();
let isFlushScheduled = false;

/**
 * Asks the server whether the session user is connected to this person. The ids that components
 * ask for in the same tick go in one bulk request: a marketplace results page shows a network
 * control on each card, and one request for each card would be an N+1 across the network.
 * An id is asked once until its request ends; the answer goes into the network connection slice.
 */
export function requestNetworkConnectionStatus(dispatch: AppDispatch, userId: Guid): void {
  if (pendingUserIds.has(userId.value) || requestedUserIds.has(userId.value)) return;
  pendingUserIds.add(userId.value);
  if (isFlushScheduled) return;
  isFlushScheduled = true;
  setTimeout(() => flushNetworkConnectionStatusRequests(dispatch), 0);
}

function flushNetworkConnectionStatusRequests(dispatch: AppDispatch): void {
  isFlushScheduled = false;
  const userIds = [...pendingUserIds];
  pendingUserIds.clear();

  for (let start = 0; start < userIds.length; start += NETWORK_CONNECTION_STATUS_BATCH_SIZE) {
    const batch = userIds.slice(start, start + NETWORK_CONNECTION_STATUS_BATCH_SIZE);
    for (const userId of batch) requestedUserIds.add(userId);
    const request = dispatch(
      userNetworkConnectionsApi.endpoints.getUserNetworkConnectionsByUserIds.initiate(
        { userIds: batch.map((userId) => new Guid(userId)) },
        // Asked only when the slice has no answer, so a cached reply is never the one wanted. RTK
        // Query keeps the last reply for these ids a minute after the request ends, and without
        // this would return it unsent: a status forgotten after an Ably change would stay unknown.
        { forceRefetch: true },
      ),
    );
    void request.finally(() => {
      for (const userId of batch) requestedUserIds.delete(userId);
      request.unsubscribe();
    });
  }
}

/**
 * Whether the session user is connected to this person: true, false, or undefined while the
 * answer loads. Pass no id, or `skip`, to ask nothing (for example, for the session user).
 */
export default function useNetworkConnectionStatus(userId?: Guid, options?: { skip?: boolean }): boolean | undefined {
  const dispatch = useAttorneyHubDispatch();
  const status = useSelector(getNetworkConnectionStatusByUserId(userId));
  const skip = options?.skip ?? false;
  const userIdValue = userId?.value;

  useEffect(() => {
    if (skip || !userIdValue || status !== undefined) return;
    requestNetworkConnectionStatus(dispatch, new Guid(userIdValue));
  }, [dispatch, skip, status, userIdValue]);

  return skip ? undefined : status;
}
